import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { modalTypes } from "constants/types";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { removeErrorMessage } from "slices/app/errorSlice";
import { isNameValid } from "utils";
import isNullishOrBlank from "utils/isNullishOrBlank";

import styles from "./TextFieldModal.module.scss";

const TextFieldModal = ({
  inputText,
  subHeading,
  title,
  placeholderText,
  buttonText,
  errorMessage,
  onOk,
  selectTextOnInitialLoad,
  autoFocus = false,
}) => {
  const dispatch = useDispatch();
  const [text, setText] = useState(inputText || "");
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const externalError = useSelector((state) => state.error.errorMessage);

  const handleClose = () => {
    dispatch(removeErrorMessage());
    dispatch(hideModal(modalTypes.TEXT_FIELD_MODAL));
  };

  const isDisabled = () => text.trim() === "" || error !== "";

  const onSubmit = () => {
    dispatch(removeErrorMessage());
    if (!isDisabled()) {
      onOk(text.trim());
    }
  };

  const onEnterKeyDown = ({ key }) => {
    dispatch(removeErrorMessage());
    if (key === "Enter") {
      onSubmit();
    }
  };

  useEffect(() => {
    if (text.trim().length && !isNameValid(text)) {
      setError(errorMessage);
    } else {
      setError("");
    }
  }, [text]);

  useEffect(() => {
    const input = inputRef.current;
    if (selectTextOnInitialLoad && input) {
      const textLength = input.value.length;
      input.focus();
      input.setSelectionRange(0, textLength);
    }
  }, []);

  return (
    <Modal
      data-testid="text-field-modal"
      className="fade-in"
      show
      onDismiss={handleClose}
      backdrop
      centered
      onKeyDown={onEnterKeyDown}
    >
      <Modal.Header>
        <b>{title}</b>
      </Modal.Header>
      <Modal.Body>
        {subHeading ? (
          <div className="text_field_modal__sub_heading">{subHeading}</div>
        ) : null}
        <input
          type="text"
          required
          autoFocus={autoFocus}
          placeholder={placeholderText}
          value={text}
          onChange={(e) => {
            dispatch(removeErrorMessage());
            setText(e.target.value);
          }}
          ref={inputRef}
          className="form__field_text"
        />
        {(!isNullishOrBlank(error) || !isNullishOrBlank(externalError)) && (
          <span className={styles["text_field_modal__name_error"]}>
            {error || externalError}
          </span>
        )}
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancelButton"
          variant={NewButtonVariant.l2}
          onClick={handleClose}
        >
          Cancel
        </NewButton>

        <NewButton
          variant={NewButtonVariant.l1}
          onClick={onSubmit}
          disabled={isDisabled()}
          data-testid="text-field-modal-button"
        >
          {buttonText || "Ok"}
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default TextFieldModal;

TextFieldModal.propTypes = {
  inputText: PropTypes.string,
  subHeading: PropTypes.string,
  title: PropTypes.string.isRequired,
  placeholderText: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  errorMessage: PropTypes.string.isRequired,
  onOk: PropTypes.func.isRequired,
  selectTextOnInitialLoad: PropTypes.bool,
  autoFocus: PropTypes.bool,
};
